/* Dignity — Shopify Inbox "Live chat" launcher in the top-right corner on
   phones, on every page (16 Sep 2026). Desktop is untouched.

   Inbox injects its launcher late and keeps most of it in a shadow root, so a
   stylesheet cannot reliably reach it (a CSS-only attempt on the home page did
   not move it). This script finds the element that actually pins the launcher
   to the screen — the fixed box in the light DOM or an open shadow root that
   sits in the bottom-right corner — and writes top/bottom on it inline with
   !important, which outranks the app's own rules.

   While the chat is OPEN (Inbox sets is-open="true", or the fixed box grows
   past 40% of the screen) the app's own values are put back, so the chat window
   opens exactly where and how Inbox draws it. Closing it puts the launcher back
   in the corner. Loaded from snippets/dignity-header-style.liquid. */
(() => {
  if (window.__dignityChatPosition) return;
  window.__dignityChatPosition = true;

  const TOP = 12; // px from the top edge (plus the notch inset)
  const phone = window.matchMedia('(max-width: 749px)');
  const HOSTS = 'inbox-online-store-chat, #shopify-chat, #ShopifyChat, iframe[id*="chat" i], iframe[title*="chat" i]';
  let moved = null; // the element currently carrying our overrides
  let scheduled = 0;

  const isFixed = (el) => el instanceof Element && getComputedStyle(el).position === 'fixed';

  /* All candidate elements: the hosts, their light-DOM descendants and, where
     the shadow root is open, everything inside it. */
  function candidates() {
    const out = [];
    document.querySelectorAll(HOSTS).forEach((host) => {
      if (host.closest('.dme, [class*="dme-"]')) return;
      out.push(host);
      host.querySelectorAll('*').forEach((el) => out.push(el));
      if (host.shadowRoot) host.shadowRoot.querySelectorAll('*').forEach((el) => out.push(el));
    });
    return out;
  }

  function hostOf(el) {
    const root = el.getRootNode && el.getRootNode();
    const base = root && root.host ? root.host : el;
    return base.closest ? base.closest('inbox-online-store-chat') || base : base;
  }

  function isOpen(el) {
    const host = hostOf(el);
    if (host && host.getAttribute && host.getAttribute('is-open') === 'true') return true;
    const r = el.getBoundingClientRect();
    return r.height > window.innerHeight * 0.4;
  }

  /* The launcher's fixed box: fixed, visible, in the lower-right of the screen.
     The outermost such box wins, so moving it carries everything inside. */
  function findLauncher() {
    const vw = window.innerWidth;
    const vh = window.innerHeight;
    let best = null;
    for (const el of candidates()) {
      if (!isFixed(el)) continue;
      const r = el.getBoundingClientRect();
      if (r.width < 1 || r.height < 1) continue;
      const anchoredRight = r.right > vw * 0.55;
      const anchoredLow = r.bottom > vh * 0.5 || el === moved;
      if (!anchoredRight || !anchoredLow) continue;
      if (!best || el.contains(best)) best = el;
    }
    return best || moved;
  }

  /* The app's own inline top/bottom, saved the first time we touch the box so
     they can be put back exactly when the chat opens (or on desktop). */
  const saved = new WeakMap();
  function remember(el) {
    if (saved.has(el)) return;
    saved.set(el, {
      top: [el.style.getPropertyValue('top'), el.style.getPropertyPriority('top')],
      bottom: [el.style.getPropertyValue('bottom'), el.style.getPropertyPriority('bottom')],
    });
  }

  function clear(el) {
    if (!el || !saved.has(el) || !el.dataset.dignityChatTop) return;
    const o = saved.get(el);
    ['top', 'bottom'].forEach((prop) => {
      if (o[prop][0]) el.style.setProperty(prop, o[prop][0], o[prop][1]);
      else el.style.removeProperty(prop);
    });
    delete el.dataset.dignityChatTop;
  }

  function apply() {
    scheduled = 0;
    if (!phone.matches) {
      clear(moved);
      moved = null;
      return;
    }
    const el = findLauncher();
    if (!el) return;
    if (moved && moved !== el) clear(moved);
    if (isOpen(el)) {
      clear(el);
      moved = el;
      return;
    }
    if (!el.dataset.dignityChatTop) {
      saved.delete(el);
      remember(el);
    }
    const top = `calc(${TOP}px + env(safe-area-inset-top, 0px))`;
    if (el.style.getPropertyValue('top') !== top || el.style.getPropertyPriority('top') !== 'important') {
      el.style.setProperty('top', top, 'important');
    }
    if (el.style.getPropertyValue('bottom') !== 'auto' || el.style.getPropertyPriority('bottom') !== 'important') {
      el.style.setProperty('bottom', 'auto', 'important');
    }
    if (el.dataset) el.dataset.dignityChatTop = '1';
    moved = el;
  }

  function schedule() {
    if (!scheduled) scheduled = requestAnimationFrame(apply);
  }

  /* Inbox arrives late and changes its own attributes when it opens and
     closes, so watch the page (and the hosts) and re-check on every change. */
  const watched = new WeakSet();
  const observer = new MutationObserver(() => {
    document.querySelectorAll(HOSTS).forEach((host) => {
      if (watched.has(host)) return;
      watched.add(host);
      observer.observe(host, { attributes: true, subtree: true, childList: true });
      if (host.shadowRoot) observer.observe(host.shadowRoot, { attributes: true, subtree: true, childList: true });
    });
    schedule();
  });

  function start() {
    observer.observe(document.body, { childList: true, subtree: true });
    schedule();
    // Belt and braces for the first minute while the app boots.
    let n = 0;
    const timer = setInterval(() => {
      schedule();
      if (++n > 60) clearInterval(timer);
    }, 1000);
    window.addEventListener('resize', schedule);
    phone.addEventListener('change', schedule);
    document.addEventListener('click', () => setTimeout(schedule, 60), true);
  }

  if (document.body) start();
  else document.addEventListener('DOMContentLoaded', start);
})();
